import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { LightArt } from "./LightArt";
import { api } from "../lib/api";

const PLACEHOLDER_WORKS = [
  { id: "p1", title: "Nachtspur", year: "MMXXII", span: "md:col-span-2 md:row-span-2", variant: 1 },
  { id: "p2", title: "Sphären", year: "MMXXIII", span: "md:col-span-1 md:row-span-1", variant: 2 },
  { id: "p3", title: "Kaskade", year: "MMXXIII", span: "md:col-span-1 md:row-span-1", variant: 3 },
  { id: "p4", title: "Wellenlicht", year: "MMXXI", span: "md:col-span-1 md:row-span-1", variant: 5 },
  { id: "p5", title: "Blitzschrift", year: "MMXXIV", span: "md:col-span-2 md:row-span-1", variant: 4 },
  { id: "p6", title: "Sternblume", year: "MMXXIV", span: "md:col-span-1 md:row-span-2", variant: 6 },
  { id: "p7", title: "Flimmern", year: "MMXXII", span: "md:col-span-2 md:row-span-1", variant: 3 },
];

// Spans for real uploaded items (rotate through a repeating pattern)
const SPANS = [
  "md:col-span-2 md:row-span-2",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-2 md:row-span-1",
  "md:col-span-1 md:row-span-2",
  "md:col-span-2 md:row-span-1",
];

export const Gallery = () => {
  const [items, setItems] = useState(null); // null = loading

  useEffect(() => {
    api
      .get("/gallery")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  // Decide what to render
  const usingReal = Array.isArray(items) && items.length > 0;
  const works = usingReal
    ? items.map((it, i) => ({
        id: it.id,
        title: it.title,
        year: it.year || "",
        span: SPANS[i % SPANS.length],
        image_base64: it.image_base64,
      }))
    : PLACEHOLDER_WORKS;

  return (
    <section
      id="galerie"
      data-testid="gallery-section"
      className="relative py-28 md:py-40 px-6 lg:px-12 border-t border-[#D4AF37]/10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
          <div>
            <span className="gold-divider mb-6" data-testid="gallery-eyebrow">
              Werkauswahl
            </span>
            <h2
              data-testid="gallery-heading"
              className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#E5E5E5] mt-6"
            >
              Licht<span className="italic gold-text">werke</span>
            </h2>
          </div>
          <p
            className="max-w-sm font-sans font-light text-sm md:text-base text-[#E5E5E5]/70 leading-relaxed"
            data-testid="gallery-intro"
          >
            Eine Auswahl aus Nächten, in denen die Kamera atmete und das Licht
            eine eigene Sprache fand.
          </p>
        </div>

        <div
          data-testid="gallery-grid"
          className="grid grid-cols-1 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[200px] gap-4 md:gap-6"
        >
          {works.map((w, idx) => (
            <motion.figure
              key={w.id}
              data-testid={`gallery-item-${idx + 1}`}
              initial={{ opacity: 0, filter: "blur(24px) brightness(0.1)" }}
              whileInView={{ opacity: 1, filter: "blur(0px) brightness(1)" }}
              transition={{ duration: 1.6, delay: (idx % 4) * 0.12, ease: "easeOut" }}
              viewport={{ once: true, margin: "-60px" }}
              className={`group relative overflow-hidden border border-[#D4AF37]/12 ${w.span}`}
              style={{
                gridRow: `span ${w.span.includes("row-span-2") ? 2 : 1}`,
              }}
            >
              {w.image_base64 ? (
                <img
                  src={w.image_base64}
                  alt={w.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-105"
                />
              ) : (
                <LightArt variant={w.variant || (idx % 6) + 1} />
              )}

              <div
                className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                style={{
                  boxShadow: "inset 0 0 80px rgba(212,175,55,0.35)",
                  background:
                    "radial-gradient(circle at center, rgba(253,224,71,0.14), transparent 65%)",
                }}
              />

              <figcaption className="absolute inset-x-0 bottom-0 p-4 md:p-6 flex items-end justify-between gap-4 bg-gradient-to-t from-[#090909]/95 via-[#090909]/30 to-transparent">
                <div>
                  <p className="font-serif italic text-[#E5E5E5] text-lg md:text-xl leading-none">
                    {w.title}
                  </p>
                  {w.year && (
                    <p className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]/70 font-sans mt-2">
                      {w.year}
                    </p>
                  )}
                </div>
                <span className="text-[#D4AF37]/70 font-serif text-xs md:text-sm tabular-nums">
                  · {String(idx + 1).padStart(2, "0")}
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <div className="mt-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <p
            className="text-xs uppercase tracking-[0.4em] text-[#A1A1AA] font-sans font-light"
            data-testid="gallery-footnote"
          >
            Jedes Bild — eine einzige Belichtung. Keine Bearbeitung.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/galerie"
              data-testid="gallery-more-cta"
              className="btn-gold inline-flex items-center gap-3 px-8 py-3 font-sans font-light tracking-widest text-xs uppercase"
            >
              Alle Werke ansehen <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
            <Link
              to="/shop"
              data-testid="gallery-shop-cta"
              className="text-[11px] uppercase tracking-[0.35em] font-sans font-light text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
            >
              Zum Shop →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
